import { useEffect, useMemo, useRef, useState } from "react";
import {
  MIN_VISIBLE_COLS, DEFAULT_COL_WIDTH, DEFAULT_ROW_HEIGHT,
} from "../constants";

/**
 * useSheetModel
 * Tracks the active sheet and exposes its derived data (rows, widths,
 * heights, styles, merges, links, etc.).  Falls back to sensible defaults
 * when the model is missing a particular field.  Also resets the active
 * sheet to the first available one whenever the workbook reloads.
 */
export default function useSheetModel({ sheetNames, sheetDataByName }) {
  const [activeSheet, setActiveSheet] = useState("");

  // When the workbook reloads, snap to the first sheet (or keep the
  // current one if it still exists).
  useEffect(() => {
    setActiveSheet((prev) => (prev && sheetNames.includes(prev) ? prev : sheetNames[0] || ""));
  }, [sheetNames]);

  const activeModel = useMemo(() => {
    if (!activeSheet) return null;
    return sheetDataByName[activeSheet] || null;
  }, [activeSheet, sheetDataByName]);

  const normalizedRows = useMemo(() => activeModel?.rows || [], [activeModel?.rows]);
  const maxCols = activeModel?.maxCols || MIN_VISIBLE_COLS;
  // IMPORTANT: every fallback below must be memoized.  Returning a fresh
  // array/object literal on each render would change the reference on
  // every pass, which cascades into useMemo deps downstream (e.g.
  // useFreezePanes' seedRowOffsets) and triggers an infinite setState
  // loop ("Maximum update depth exceeded").
  const colWidths = useMemo(
    () => activeModel?.colWidths || new Array(maxCols).fill(DEFAULT_COL_WIDTH),
    [activeModel?.colWidths, maxCols],
  );
  const rowHeights = useMemo(
    () => activeModel?.rowHeights || new Array(normalizedRows.length).fill(DEFAULT_ROW_HEIGHT),
    [activeModel?.rowHeights, normalizedRows.length],
  );
  const cellStyles = useMemo(() => activeModel?.styles || {}, [activeModel?.styles]);
  const cellRichHtml = useMemo(() => activeModel?.richHtml || {}, [activeModel?.richHtml]);
  const sheetImages = useMemo(() => activeModel?.images || [], [activeModel?.images]);
  const sheetCharts = useMemo(() => activeModel?.charts || [], [activeModel?.charts]);
  const cellMerges = useMemo(() => activeModel?.merges || {}, [activeModel?.merges]);
  const cellLinks = useMemo(() => activeModel?.links || {}, [activeModel?.links]);

  // Track the previous sheet to detect transitions for hooks that
  // need to checkpoint per-sheet state on tab switch.
  const prevSheetRef = useRef(activeSheet);
  useEffect(() => { prevSheetRef.current = activeSheet; }, [activeSheet]);

  return {
    activeSheet, setActiveSheet, prevSheetRef,
    activeModel, normalizedRows, maxCols,
    colWidths, rowHeights, cellStyles, cellRichHtml,
    sheetImages, sheetCharts, cellMerges, cellLinks,
  };
}
